import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CalendarOptions, EventClickArg, EventInput } from '@fullcalendar/core';
import frLocale from '@fullcalendar/core/locales/fr';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Observable, Subscription, take } from 'rxjs';
import { ActivityResponse } from 'src/app/model/dto/ActivityResponse';
import { PlateformeLMSService } from 'src/app/modules/plateforme-lms/services/plateforme-lms.service';
import { BreadcrumbItem } from 'src/app/modules/shared/components/breadcrumb/breadcrumb.component';
import { ActivityService } from '../../services/activity-services';

// ===== REFONTE : agenda des activités, un seul composant pour les deux espaces =====
// Remplace AgendaApprenantComponent et AgendaForcenComponent.
// L'espace vient de la route : data: { espace: 'apprenant' } ou data: { espace: 'gestionnaire' }.
// - statut en couleur : à confirmer (orange), confirmée (vert), en ligne (bleu), à venir (violet), passée / fermée (gris)
// - vue Calendrier ou Liste (Liste par défaut sur mobile)
// - une seule fenêtre de détails : la confirmation se fait dedans, sans 2e popup
// - lien direct depuis le mail : #/dashboard/agenda/<espace>?activite=123 ouvre l'activité

export type EspaceAgenda = 'apprenant' | 'gestionnaire';

/** Ce qui change d'un espace à l'autre */
interface ConfigEspace {
  titre: (profil: number) => string;
  sousTitre: string;
  filAriane: BreadcrumbItem[];
  /** Types pour lesquels on peut confirmer sa participation */
  typesAConfirmer: string[];
  profilAutorise: (profil: number) => boolean;
  source: (
    service: ActivityService,
    iuf: string,
  ) => Observable<ActivityResponse[]>;
}

const ESPACES: Record<EspaceAgenda, ConfigEspace> = {
  apprenant: {
    titre: (profil) => (profil === 1 ? 'Mon Sigui' : 'Mon Agenda'),
    sousTitre: 'Vos activités Force-N à venir et vos confirmations',
    filAriane: [
      { label: 'OUTILS' },
      { label: 'AGENDA', url: 'dashboard/agenda/apprenant' },
    ],
    typesAConfirmer: [
      'ATELIER_FORCEN',
      'BOOTCAMP',
      'MINIJPO',
      'JPO',
      'MASTERCLASS',
      'PAR_ZONE_RESIDENCE',
    ],
    profilAutorise: (profil) => [1, 2, 3].includes(profil),
    source: (service, iuf) => service.allBeneficiaryActivitiesByIuf(iuf),
  },
  gestionnaire: {
    titre: () => 'Mon Agenda',
    sousTitre: 'Les activités de FORCE-N à venir et vos confirmations',
    filAriane: [
      { label: 'OUTILS' },
      { label: 'ACTIVITÉS FORCE-N', url: 'dashboard/agenda/gestionnaire' },
    ],
    typesAConfirmer: ['ATELIER_FORCEN'],
    profilAutorise: (profil) => profil !== 1,
    source: (service, iuf) => service.allForceNActivityByGestionnairePaged(iuf),
  },
};

const LIBELLES_TYPE: Record<string, string> = {
  ATELIER_FORCEN: 'Atelier Force-N',
  BOOTCAMP: 'Bootcamp',
  MINIJPO: 'Mini JPO',
  JPO: 'JPO',
  MASTERCLASS: 'Masterclass',
  PAR_ZONE_RESIDENCE: 'Par zone de résidence',
  WEBINAIRE: 'Webinaire',
};

export type StatutActivite =
  | 'a-confirmer'
  | 'confirmee'
  | 'en-ligne'
  | 'a-venir'
  | 'passee'
  | 'fermee';

export interface ActiviteAgenda {
  id: number;
  nom: string;
  description: string;
  adresse: string;
  lien: string;
  heure: string;
  debut: Date | null;
  fin: Date | null;
  type: string;
  confirmee: boolean;
  fermee: boolean;
}

export interface GroupeMois {
  libelle: string;
  activites: ActiviteAgenda[];
}

type EtatConfirmation = 'attente' | 'envoi' | 'ok' | 'erreur';

@Component({
  selector: 'app-agenda-activites',
  templateUrl: './agenda-activites.component.html',
  styleUrls: ['./agenda-activites.component.css'],
  // Les styles de FullCalendar sont chargés après ceux du composant : sans encapsulation, on peut les surcharger.
  // Toutes les règles du CSS sont préfixées par .ag-page, elles ne débordent pas sur le reste de l'appli.
  encapsulation: ViewEncapsulation.None,
})
export class AgendaActivitesComponent implements OnInit, OnDestroy {
  /** Espace affiché, lu dans la route (apprenant par défaut) */
  espace: EspaceAgenda = 'apprenant';
  private config: ConfigEspace = ESPACES.apprenant;
  breadcrumbItems: BreadcrumbItem[] = ESPACES.apprenant.filAriane;

  profil!: number;
  iuf!: string;
  emailUtilisateur = '';

  activites: ActiviteAgenda[] = [];
  chargement = true;
  erreurChargement = false;

  /** Message affiché quand le chargement échoue */
  messageErreur = '';
  /** Faux pour une erreur d'identification : réessayer ne changerait rien */
  peutReessayer = true;

  /** Liste par défaut sur petit écran : une grille de mois y est illisible */
  vue: 'calendrier' | 'liste' =
    window.innerWidth < 768 ? 'liste' : 'calendrier';

  // Fenêtre de détails
  activiteOuverte: ActiviteAgenda | null = null;
  etatConfirmation: EtatConfirmation = 'attente';

  /** Message affiché quand le lien du mail ne mène à rien d'exploitable */
  messageLien: string | null = null;

  /** Id reçu dans l'URL (?activite=…), traité une fois les activités chargées */
  private idDemande: number | null = null;
  private abonnements = new Subscription();
  /** Raison du refus renvoyée par le back (non invité, activité close…) */
  messageConfirmation: string | null = null;

  calendarOptions: CalendarOptions = {
    locale: frLocale,
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay',
    },
    initialView: 'dayGridMonth',
    height: 'auto',
    dayMaxEvents: 3,
    eventMaxStack: 3,
    events: [],
    eventClick: (arg: EventClickArg) => {
      arg.jsEvent.preventDefault();
      this.ouvrirParId(Number(arg.event.extendedProps['idActivity']));
    },
  };

  constructor(
    private activityService: ActivityService,
    private plateformeLMSService: PlateformeLMSService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    const user = JSON.parse(sessionStorage.getItem('user') ?? '{}');
    this.iuf = user?.iuf;
    this.profil = Number(user?.profile?.id);
    this.emailUtilisateur = user?.email ?? '';

    const espace = this.route.snapshot?.data?.['espace'];
    this.espace = espace === 'gestionnaire' ? 'gestionnaire' : 'apprenant';
    this.config = ESPACES[this.espace];
    this.breadcrumbItems = this.config.filAriane;
  }

  ngOnInit(): void {
    this.abonnements.add(
      this.route.queryParamMap.subscribe((params) => {
        const id = Number(params.get('activite'));
        if (id) {
          this.idDemande = id;
          if (!this.chargement) this.traiterLienDirect();
        }
      }),
    );
    this.chargerActivites();
  }

  ngOnDestroy(): void {
    this.abonnements.unsubscribe();
  }

  // ---------------------------------------------------------------- selon l'espace
  get titre(): string {
    return this.config.titre(this.profil);
  }

  get sousTitre(): string {
    return this.config.sousTitre;
  }

  get profilAutorise(): boolean {
    return this.config.profilAutorise(this.profil);
  }

  // ---------------------------------------------------------------- chargement
  chargerActivites(): void {
    this.chargement = true;
    this.erreurChargement = false;
    this.config
      .source(this.activityService, this.iuf)
      .pipe(take(1))
      .subscribe({
        next: (reponse: ActivityResponse[]) => {
          this.activites = (reponse ?? []).map((a) => this.versActivite(a));
          this.majCalendrier();
          this.chargement = false;
          this.traiterLienDirect();
        },
        // ===== MODIF : message selon le type d'erreur =====
        error: (err: any) => {
          this.chargement = false;
          this.erreurChargement = true;
          const nonIdentifie = err?.status === 401 || err?.status === 403;
          this.peutReessayer = !nonIdentifie;
          this.messageErreur = nonIdentifie
            ? "Votre compte n'est pas reconnu. Déconnectez-vous puis reconnectez-vous ; si le problème continue, contactez l'équipe Force-N."
            : 'Impossible de charger vos activités. Vérifiez votre connexion puis réessayez.';
        },
        // ===== FIN MODIF =====
      });
  }

  private versActivite(a: ActivityResponse): ActiviteAgenda {
    return {
      id: Number(a.id),
      // Le modèle déclare ces champs en « String » (objet) : on les convertit en « string » (primitive)
      nom: this.texte(a.nomActivity),
      description: this.texte(a.description),
      adresse: this.texte(a.adresse),
      lien: this.texte(a.accessLink),
      heure: this.texte(a.hours),
      debut: this.versDate(a.openDate),
      fin: this.versDate(a.closeDate),
      type: this.texte(a.activityType),
      confirmee: !!a.confirmed,
      fermee: !!a.isClosed,
    };
  }

  private texte(valeur: unknown): string {
    return valeur === null || valeur === undefined ? '' : String(valeur);
  }

  private versDate(valeur: unknown): Date | null {
    if (!valeur) return null;
    const d = new Date(valeur as string | number | Date);
    return isNaN(d.getTime()) ? null : d;
  }

  private jourSuivant(d: Date): Date {
    const suivant = new Date(d);
    suivant.setDate(suivant.getDate() + 1);
    return suivant;
  }

  private majCalendrier(dateInitiale?: Date): void {
    const evenements: EventInput[] = this.activites.map((a) => ({
      title: a.nom,
      start: a.debut ?? undefined,
      // En « journée entière », FullCalendar exclut le jour de fin : on ajoute un jour pour que le dernier jour s'affiche
      end: a.fin ? this.jourSuivant(a.fin) : undefined,
      allDay: true,
      classNames: ['ag-ev', 'ag-ev--' + this.statut(a)],
      extendedProps: { idActivity: a.id },
    }));
    this.calendarOptions = {
      ...this.calendarOptions,
      events: evenements,
      ...(dateInitiale ? { initialDate: dateInitiale } : {}),
    };
  }

  // ---------------------------------------------------------------- lien direct depuis le mail
  private traiterLienDirect(): void {
    if (this.idDemande === null) return;
    const id = this.idDemande;
    this.idDemande = null;

    const activite = this.activites.find((a) => a.id === id);
    if (!activite) {
      this.messageLien =
        "Cette activité ne figure pas dans votre agenda. Vérifiez que vous êtes connecté(e) avec l'adresse qui a reçu l'invitation.";
    } else {
      // Le calendrier se place sur le mois de l'activité, puis on ouvre ses détails
      if (activite.debut) this.majCalendrier(activite.debut);
      this.ouvrir(activite);
    }

    // On retire ?activite= de l'URL : un rafraîchissement ne rouvre pas la fenêtre
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { activite: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  // ---------------------------------------------------------------- statuts
  statut(a: ActiviteAgenda): StatutActivite {
    if (a.fermee) return 'fermee';
    if (this.estPassee(a)) return 'passee';
    // Pas de confirmation pour ce type : « En ligne » s'il y a un lien de réunion, sinon simplement « À venir »
    if (!this.aConfirmer(a)) return a.lien ? 'en-ligne' : 'a-venir';
    return a.confirmee ? 'confirmee' : 'a-confirmer';
  }

  libelleStatut(a: ActiviteAgenda): string {
    switch (this.statut(a)) {
      case 'a-confirmer':
        return 'À confirmer';
      case 'confirmee':
        return 'Confirmée';
      case 'fermee':
        return 'Fermée';
      case 'passee':
        return 'Passée';
      case 'a-venir':
        return 'À venir';
      default:
        return 'En ligne';
    }
  }

  libelleType(a: ActiviteAgenda): string {
    return LIBELLES_TYPE[a.type] ?? a.type;
  }

  private aConfirmer(a: ActiviteAgenda): boolean {
    return this.config.typesAConfirmer.includes(a.type);
  }

  private estPassee(a: ActiviteAgenda): boolean {
    const reference = a.fin ?? a.debut;
    if (!reference) return false;
    const debutDuJour = new Date();
    debutDuJour.setHours(0, 0, 0, 0);
    return reference.getTime() < debutDuJour.getTime();
  }

  // ---------------------------------------------------------------- chiffres et listes
  get aConfirmerListe(): ActiviteAgenda[] {
    return this.activites
      .filter((a) => this.statut(a) === 'a-confirmer')
      .sort((x, y) => (x.debut?.getTime() ?? 0) - (y.debut?.getTime() ?? 0));
  }

  get nbConfirmees(): number {
    return this.activites.filter((a) => this.statut(a) === 'confirmee').length;
  }

  get nbAVenir(): number {
    return this.activites.filter((a) => !this.estPassee(a) && !a.fermee).length;
  }

  /** Vue Liste : activités à venir regroupées par mois, triées par date */
  get groupesAVenir(): GroupeMois[] {
    const aVenir = this.activites
      .filter((a) => !this.estPassee(a) && !a.fermee)
      .sort((x, y) => (x.debut?.getTime() ?? 0) - (y.debut?.getTime() ?? 0));
    const groupes: GroupeMois[] = [];
    for (const a of aVenir) {
      const libelle = a.debut
        ? a.debut.toLocaleDateString('fr-FR', {
            month: 'long',
            year: 'numeric',
          })
        : 'Date à préciser';
      let groupe = groupes.find((g) => g.libelle === libelle);
      if (!groupe) {
        groupe = { libelle, activites: [] };
        groupes.push(groupe);
      }
      groupe.activites.push(a);
    }
    return groupes;
  }

  /** Vue Liste : les plus récentes d'abord */
  get passees(): ActiviteAgenda[] {
    return this.activites
      .filter((a) => this.estPassee(a) || a.fermee)
      .sort((x, y) => (y.debut?.getTime() ?? 0) - (x.debut?.getTime() ?? 0));
  }

  trackParId(_: number, a: ActiviteAgenda): number {
    return a.id;
  }

  // ---------------------------------------------------------------- fenêtre de détails
  /** Utilisé par le gabarit des évènements du calendrier */
  activiteDe(id: unknown): ActiviteAgenda | undefined {
    return this.activites.find((a) => a.id === Number(id));
  }

  ouvrirParId(id: number): void {
    const activite = this.activiteDe(id);
    if (activite) this.ouvrir(activite);
  }

  ouvrir(activite: ActiviteAgenda): void {
    this.activiteOuverte = activite;
    this.etatConfirmation = 'attente';
    this.messageConfirmation = null; // ===== AJOUT : on efface le refus éventuel d'une activité précédente =====
  }

  fermer(): void {
    if (this.etatConfirmation === 'envoi') return;
    this.activiteOuverte = null;
  }

  @HostListener('document:keydown.escape')
  surEchap(): void {
    if (this.activiteOuverte) this.fermer();
  }

  confirmer(): void {
    const activite = this.activiteOuverte;
    if (!activite || this.etatConfirmation === 'envoi') return;
    this.etatConfirmation = 'envoi';

    this.activityService
      .confirmParticipation(activite.id)
      .pipe(take(1))
      .subscribe({
        next: (reponse: any) => {
          if (reponse?.status === 200) {
            activite.confirmee = true;
            this.etatConfirmation = 'ok';
            this.majCalendrier();
          } else {
            this.etatConfirmation = 'erreur';
          }
        },
        // ===== MODIF : afficher la raison du refus renvoyée par le back =====
        error: (err: any) => {
          this.etatConfirmation = 'erreur';
          this.messageConfirmation = err?.error?.message ?? null;
        },
      });
  }

  // ---------------------------------------------------------------- affichage
  formatHeure(heure: string): string {
    if (!heure) return '';
    const [h, m] = heure.split(':');
    return m ? `${Number(h)} h ${m}` : heure;
  }

  /** « jeudi 5 novembre 2026 » → « Jeudi 5 novembre 2026 » (sans mettre de majuscule au mois) */
  majuscule(texte: string | null): string {
    return texte ? texte.charAt(0).toUpperCase() + texte.slice(1) : '';
  }

  lienCarte(adresse: string): string {
    return (
      'https://www.google.com/maps/search/?api=1&query=' +
      encodeURIComponent(adresse)
    );
  }

  memeJour(a: ActiviteAgenda): boolean {
    return (
      !a.fin || !a.debut || a.fin.toDateString() === a.debut.toDateString()
    );
  }
}
// ===== FIN REFONTE =====
